import { PhoneCall, ShieldCheck, Sparkles, Mic, Clock } from "lucide-react";

export const Window = ({ title, children, dark = true, className = "" }) => (
  <div className={`rounded-2xl overflow-hidden border shadow-2xl ${dark ? "bg-[#0E1422] border-white/10 shadow-black/40" : "bg-white border-slate-200 shadow-slate-900/10"} ${className}`}>
    <div className={`flex items-center gap-1.5 px-4 py-2.5 border-b ${dark ? "border-white/5 bg-white/[0.02]" : "border-slate-100 bg-slate-50"}`}>
      <span className="w-2.5 h-2.5 rounded-full bg-rose-400/80" /><span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" /><span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
      <span className={`ml-3 text-[11px] font-mono truncate ${dark ? "text-slate-500" : "text-slate-400"}`}>{title}</span>
    </div>
    {children}
  </div>
);

const Bar = ({ label, v, color = "bg-indigo-500", dark = true }) => (
  <div>
    <div className={`flex justify-between text-xs mb-1.5 ${dark ? "text-slate-400" : "text-slate-500"}`}><span>{label}</span><span className={`font-mono ${dark ? "text-slate-200" : "text-slate-800"}`}>{v}</span></div>
    <div className={`h-1.5 rounded-full ${dark ? "bg-white/5" : "bg-slate-100"}`}><div className={`h-full rounded-full ${color} rs-grow`} style={{ width: `${v}%` }} /></div>
  </div>
);

export function PhoneMock() {
  const lines = [
    ["AI", "Thanks for taking the call, David. Is now still a good time, or should I call back later?"],
    ["David", "Now works, go ahead."],
    ["AI", "Great. Can you walk me through how you've handled PostgreSQL replication in production?"],
    ["David", "At my last role I set up streaming replication with PgBouncer pooling for about 15k concurrent queries…"],
  ];
  return (
    <Window title="phone-screen · Senior Backend Engineer">
      <div className="p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500/15 grid place-items-center"><PhoneCall className="w-5 h-5 text-emerald-400" /></div>
          <div className="flex-1"><div className="text-sm text-white font-medium">Live call · David Chen</div><div className="text-xs text-slate-500 font-mono">+1 ••• ••• 4821 · 02:44</div></div>
          <div className="flex items-end gap-0.5 h-6">{[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className="w-1 bg-emerald-400 rounded-full rs-wave" style={{ animationDelay: `${i * 0.12}s` }} />)}</div>
        </div>
        <div className="space-y-2.5">
          {lines.map(([who, t], i) => (
            <div key={i} className={`flex ${who === "AI" ? "" : "justify-end"}`}>
              <div className={`max-w-[85%] text-xs leading-relaxed px-3 py-2 rounded-xl ${who === "AI" ? "bg-indigo-500/15 text-indigo-100" : "bg-white/5 text-slate-300"}`}><b className="font-semibold">{who === "AI" ? "AI Screener" : who}:</b> {t}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 text-[10px] font-mono">
          <span className="px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">Knockout: work permit ✓</span>
          <span className="px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">Ready to continue ✓</span>
        </div>
      </div>
    </Window>
  );
}

export function VideoMock() {
  return (
    <Window title="video-interview · Question 3 of 6">
      <div className="p-5 grid grid-cols-1 sm:grid-cols-5 gap-4">
        <div className="sm:col-span-3 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 aspect-[4/3] relative overflow-hidden">
          <div className="absolute inset-0 grid place-items-center"><div className="w-16 h-16 rounded-full bg-slate-700 grid place-items-center text-xl font-display text-slate-300">PR</div></div>
          <span className="absolute top-2 left-2 text-[10px] font-mono px-2 py-0.5 rounded-full bg-rose-500/90 text-white flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />REC 01:12</span>
          <div className="absolute bottom-2 left-2 right-2 flex items-end gap-0.5 h-5">{Array.from({ length: 24 }).map((_, i) => <span key={i} className="flex-1 bg-cyan-400/70 rounded-full rs-wave" style={{ animationDelay: `${(i % 6) * 0.1}s` }} />)}</div>
        </div>
        <div className="sm:col-span-2 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">AI asks</div>
          <div className="text-sm text-white leading-snug">"Tell me about a time you disagreed with a technical decision. What did you do?"</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono pt-1">Live transcript</div>
          <div className="text-xs text-slate-400 leading-relaxed">"We were choosing between Kafka and SQS. I put together a small benchmark…"<span className="inline-block w-1.5 h-3 bg-cyan-400 ml-0.5 animate-pulse align-middle" /></div>
        </div>
      </div>
    </Window>
  );
}

export function CodingMock() {
  const code = [
    ["kw", "def "], ["fn", "merge_intervals"], ["", "(intervals):"], ["nl"],
    ["", "    intervals."], ["fn", "sort"], ["", "(key="], ["kw", "lambda "], ["", "x: x[0])"], ["nl"],
    ["", "    out = []"], ["nl"],
    ["kw", "    for "], ["", "s, e "], ["kw", "in "], ["", "intervals:"], ["nl"],
    ["kw", "        if "], ["", "out "], ["kw", "and "], ["", "s <= out[-1][1]:"], ["nl"],
    ["", "            out[-1][1] = "], ["fn", "max"], ["", "(out[-1][1], e)"], ["nl"],
    ["kw", "        else"], ["", ":"], ["nl"],
    ["", "            out.append([s, e])"], ["nl"],
    ["kw", "    return "], ["", "out"],
  ];
  const cls = { kw: "text-violet-400", fn: "text-cyan-300", "": "text-slate-300" };
  return (
    <Window title="coding-round · python 3 · 27:41 left">
      <div className="grid sm:grid-cols-5">
        <pre className="sm:col-span-3 p-5 text-[12px] leading-6 font-mono overflow-x-auto">
          {code.map(([k, t], i) => (k === "nl" ? "\n" : <span key={i} className={cls[k]}>{t}</span>))}
        </pre>
        <div className="sm:col-span-2 border-t sm:border-t-0 sm:border-l border-white/5 p-5 space-y-3 text-xs">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Run output</div>
          <div className="font-mono text-emerald-300">[[1, 6], [8, 10], [15, 18]]</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono pt-2">Integrity signals</div>
          {[["Tab switches", "0"], ["Paste events", "0"], ["Keystrokes", "1,284"]].map(([l, v]) => (
            <div key={l} className="flex justify-between text-slate-400"><span>{l}</span><span className="font-mono text-slate-200">{v}</span></div>
          ))}
          <div className="flex items-center gap-1.5 text-emerald-300 pt-1"><ShieldCheck className="w-3.5 h-3.5" /> Proctoring active</div>
        </div>
      </div>
    </Window>
  );
}

export function ScorecardMock({ dark = true }) {
  return (
    <Window title="scorecard · Alex Rivera" dark={dark}>
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className={`font-display text-lg font-semibold ${dark ? "text-white" : "text-slate-900"}`}>Alex Rivera</div>
            <div className={`text-xs ${dark ? "text-slate-500" : "text-slate-500"}`}>Senior Full-Stack Engineer · Client: Nexus Systems</div>
          </div>
          <div className="text-right">
            <div className="font-display text-3xl font-bold text-indigo-500">91</div>
            <div className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 border border-emerald-500/30">STRONG HIRE</div>
          </div>
        </div>
        <div className="space-y-3">
          <Bar dark={dark} label="Phone screen" v={88} color="bg-emerald-500" />
          <Bar dark={dark} label="Video interview" v={92} color="bg-cyan-500" />
          <Bar dark={dark} label="Coding round" v={94} color="bg-indigo-500" />
        </div>
        <div className={`rounded-xl p-3 text-xs leading-relaxed ${dark ? "bg-white/[0.03] text-slate-400" : "bg-slate-50 text-slate-600"}`}>
          <div className="flex items-center gap-1.5 mb-1 font-semibold text-indigo-500"><Sparkles className="w-3.5 h-3.5" /> AI summary</div>
          Clear ownership of distributed systems work; solved the coding task cleanly with no integrity flags. Probe depth on frontend testing.
        </div>
      </div>
    </Window>
  );
}

export function AnalyticsMock() {
  const funnel = [["Invited", 100, "bg-indigo-500"], ["Screening", 72, "bg-cyan-500"], ["Incomplete", 9, "bg-amber-500"], ["Completed", 58, "bg-emerald-500"], ["Hired", 12, "bg-violet-500"]];
  const bars = [30, 45, 38, 60, 52, 75, 68, 82, 70, 90, 84, 96];
  return (
    <Window title="analytics · last 30 days">
      <div className="p-5 grid sm:grid-cols-2 gap-6">
        <div className="space-y-2.5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Hiring funnel</div>
          {funnel.map(([l, v, c]) => (
            <div key={l} className="flex items-center gap-3 text-xs">
              <span className="w-20 text-slate-400">{l}</span>
              <div className="flex-1 h-3 rounded bg-white/5"><div className={`h-full rounded ${c} rs-grow`} style={{ width: `${v}%` }} /></div>
              <span className="w-7 text-right font-mono text-slate-300">{v}</span>
            </div>
          ))}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-3">Completions per week</div>
          <div className="flex items-end gap-1.5 h-28">{bars.map((h, i) => <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-indigo-600 to-cyan-400 rs-rise" style={{ height: `${h}%`, animationDelay: `${i * 40}ms` }} />)}</div>
          <div className="mt-3 flex gap-2 text-[10px] font-mono">{["PDF", "Excel", "CSV"].map((x) => <span key={x} className="px-2 py-0.5 rounded border border-white/10 text-slate-400">{x}</span>)}</div>
        </div>
      </div>
    </Window>
  );
}

export function HeroVisual() {
  const rows = [["Priya R.", "Java Developer", "Completed", 86, "emerald"], ["Marcus V.", "DevOps Engineer", "Coding", null, "indigo"], ["Lena K.", "Data Engineer", "Phone call", null, "cyan"], ["Omar S.", "QA Automation", "Incomplete", null, "amber"]];
  const tone = { emerald: "bg-emerald-50 text-emerald-700 border-emerald-200", indigo: "bg-indigo-50 text-indigo-700 border-indigo-200", cyan: "bg-cyan-50 text-cyan-700 border-cyan-200", amber: "bg-amber-50 text-amber-700 border-amber-200" };
  return (
    <div className="relative" data-testid="hero-visual">
      <div className="absolute -inset-6 bg-gradient-to-tr from-indigo-200/50 via-cyan-100/40 to-transparent blur-3xl rounded-full" />
      <Window dark={false} title="recruitsift.com · Senior Backend Engineer" className="relative">
        <div className="p-4 sm:p-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {[["Invited", "48"], ["Screening", "21"], ["Completed", "17"], ["Hired", "3"]].map(([l, v]) => (
              <div key={l} className="rounded-xl border border-slate-100 bg-slate-50/70 p-2.5"><div className="text-[10px] text-slate-500">{l}</div><div className="font-display text-xl font-semibold text-slate-900">{v}</div></div>
            ))}
          </div>
          <div className="divide-y divide-slate-100">
            {rows.map(([n, role, st, sc, t], i) => (
              <div key={n} className="flex items-center gap-3 py-2.5 rs-fade-up" style={{ animationDelay: `${300 + i * 120}ms` }}>
                <div className="w-8 h-8 rounded-full bg-slate-900 text-white grid place-items-center text-[11px] font-semibold">{n.split(" ").map((x) => x[0]).join("")}</div>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-900 truncate">{n}</div><div className="text-[11px] text-slate-500 truncate">{role}</div></div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${tone[t]}`}>{st}</span>
                <span className="w-8 text-right font-mono text-sm text-slate-800">{sc ?? "—"}</span>
              </div>
            ))}
          </div>
        </div>
      </Window>
      <div className="hidden sm:flex absolute -left-10 -bottom-7 items-center gap-2.5 rounded-2xl bg-white border border-slate-200 shadow-xl px-3.5 py-2.5 rs-float">
        <div className="w-8 h-8 rounded-full bg-emerald-50 grid place-items-center"><Mic className="w-4 h-4 text-emerald-600" /></div>
        <div><div className="text-xs font-semibold text-slate-900">AI phone screen running</div><div className="text-[10px] text-slate-500 font-mono">Lena K. · 03:12</div></div>
      </div>
      <div className="hidden sm:flex absolute -right-6 -top-5 items-center gap-2.5 rounded-2xl bg-[#0E1422] text-white shadow-xl px-3.5 py-2.5 rs-float" style={{ animationDelay: "1.2s" }}>
        <Sparkles className="w-4 h-4 text-cyan-300" />
        <div><div className="text-xs font-semibold">Scorecard ready</div><div className="text-[10px] text-slate-400 font-mono">Priya R. · Hire · 86</div></div>
      </div>
      <div className="hidden lg:flex absolute right-10 -bottom-6 items-center gap-2 rounded-full bg-white border border-slate-200 shadow-lg px-3 py-1.5 text-[11px] text-slate-600 rs-float" style={{ animationDelay: "2s" }}>
        <Clock className="w-3.5 h-3.5 text-indigo-500" /> 24/7 · candidate's timezone
      </div>
    </div>
  );
}
